import { For, Show, onMount } from "solid-js";
import { Progress } from "../components/progress";
import { A, Link, useRouteData } from "@solidjs/router";
import { UserSession, AtelierImage, ImageType, Atelier } from "../types";
import { fetchGet } from "../library/common";
import toast from "solid-toast";
import { getArtworksCountJson, limits, loadFacultyCms } from "../app";
import { createStore } from "solid-js/store";
import { CMS } from "../components/cms/cms";
import { MultiProgress } from "../components/multi-progress";
import { AtelierImageItem } from "../components/atelier-image-item";
import { MultiProgressPanel } from "../components/multi-progress-panel";
import { SectionHeader } from "../components/section-header";
import { PageLoading } from "../components/page-loading";

type State = {
    isContentReady: boolean;
    proceedToNext: boolean;
}

export function StudentWorks() {
    const [session, setSession] = createStore<UserSession>({ } as UserSession);
    const [state, setState] = createStore<State>({ isContentReady: false, proceedToNext: false } as State);
    const [atelier, setAtelier] = createStore<Atelier>({} as Atelier);
    const [atelierImages, setAtelierImages] = createStore<AtelierImage[]>([]);
    const count = () => atelierImages.length;
    const canApply = true;
    const toastSuccess = (msg: string) => {
        toast.success(msg);
    }
    const toastError = (msg: string) => {
        toast.error(msg);
    }
    const getFacultyMembers = async (id: any) => {
        const res = await fetchGet("/AtelierApplication/GetFacultyMembersJson", { id });
        const json = await res.json();
        return json.Data as any[];
    };
    const getAtelierImages = async (id: any) => {
        const res = await fetchGet("/AtelierApplication/GetAtelierImagesJson", { id });
        const json = await res.json();
        return json.Data as AtelierImage[];
    };
    const getInstructorWorks = async (id: any) => {
        const images = await getAtelierImages(id);
        return images.filter(x => x.ImageType == ImageType.InstructorWork);
    };
    const canProceedToOtherImages = async () => {
        const studentWorksTotal = atelierImages.length;
        if (studentWorksTotal == 0) {
            const instructorWorksTotal = (await getInstructorWorks(atelier.Id)).length;
            const facultyMembersTotal = (await getFacultyMembers(atelier.Id)).length;
            const minimumInstructorWorks = facultyMembersTotal * 5;
            if (instructorWorksTotal >= minimumInstructorWorks) {
                return true;
            }
            toast.error(`If you have no student works to add at this stage you must submit at least 5 instructor works per faculty member (i.e., a minimum of ${minimumInstructorWorks} instructor works for your application).`);
            return false;
        }
        if (studentWorksTotal < limits.studentWorks.min) {
            toast.error(`You must submit at least ${limits.studentWorks.min} student works.`);
            return false;
        }
        return true;
    }
    const onDeleteClicked = async (id: string) => {
        const params = { id };
        const response = await fetchGet("/AtelierApplication/DeleteAtelierImageJson", params);
        const json = await response.json();
        if (!json.Success) {
            return toastError("Unable to remove the student work from your application at this time.");
        }
        const remaining = atelierImages.filter(x => x.Id != id);
        setAtelierImages(remaining);
        await getArtworksCountJson({ forceRefresh: true });
        toastSuccess("The student work has been removed from your application.")
    };
    onMount(async() =>{
        const routeData = await useRouteData() as any;
        setSession(routeData.session);
        setAtelierImages(routeData.atelierImages);
        setAtelier(routeData.atelier);
        const proceedToNext = await canProceedToOtherImages();
        setState({ isContentReady: true, proceedToNext });
    })
    
    return (
        <>
            <SectionHeader>
                <h1>
                    Student Works
                </h1>
                <p>
                    <a href="/"><CMS text id="nav::home">Home</CMS></a> / <A href="/introduction"><CMS text id="nav::atelier-application">ARC Atelier Application</CMS></A>
                </p>
            </SectionHeader>
            <Show when={state.isContentReady} fallback={<PageLoading />}>
                <div>
                    <Progress step={4}></Progress>
                    <Show when={canApply}>
                        <div class="row ">
                            <div class="col-md-8">
                                <p style="font-size: 1.5em;">
                                    A minimum of {limits.studentWorks.min} example images of student work, and a maximum of {limits.studentWorks.max} altogether. This may be submitted in the form of high-resolution digital images as JPG or PNG files supplied.
                                </p>
                                <p>
                                    If your school is new and you don't have examples of student work please include 5 example images of each fulltime <A href="/instructor-works">instructor's work</A>.
                                </p>
                            </div>
                            <div class="col-md-4">
                                <div class="pull-right">
                                    <MultiProgressPanel
                                        count={count}
                                        min={limits.studentWorks.min}
                                        max={limits.studentWorks.max}
                                        entity="student work"
                                    />
                                    <div style="margin-top: 40px;">
                                        <Link class="btn btn-chunky-lg btn-chunky-dark-grey" href={"/instructor-works"} style={{ "margin-right": "10px"}}>
                                            <CMS id="button::back" text>Back</CMS>
                                        </Link>
                                        <Show when={state.proceedToNext}>
                                            <A class="btn btn-chunky-lg btn-chunky-goldenrod" href={"/other-images"}><CMS id="button::next" text>Next</CMS></A>
                                        </Show>
                                    </div>
                                    <Show when={count() < limits.studentWorks.max}>
                                        <div style="margin-top: 20px;">
                                            <A class="btn btn-primary" href={"/add-student-work"}><span style="margin-right: 10px;" class="fa fa-plus-circle"></span> <CMS id="button::add-student-work" text>Add Student Work</CMS></A>
                                        </div>
                                    </Show>
                                </div>
                            </div>
                        </div>
                    </Show>
                    <div class="row">
                        <For each={atelierImages}>{(artwork) =>
                            <AtelierImageItem
                                resources={{ canApply }}
                                session={session}
                                deleteClicked={onDeleteClicked}
                                atelierImage={artwork} />
                        }</For>
                        <Show when={count() < limits.studentWorks.max}>
                            <div class="artwork-container add-artwork-container">
                                <div style="display: table; margin: 0 auto;">
                                    <div style="display: table-row;">
                                        <div style="display: table-cell; height: 300px; vertical-align: middle;">
                                            <A class="btn btn-success" style="font-size: 2em;" href={ "/add-student-work" }>
                                                <span style="margin-right: 10px;" class="fa fa-plus-circle"></span><CMS id="button::add-student-work" text>Add Student Work</CMS>
                                            </A>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </Show>
        </>
    )
}